import client from './client';

// The signed-in user: { email, firstName, lastName, role: 'admin' | 'charge',
// organization, unit: { id, name } | null }. Charge users always have a unit.
export function getMe() {
  return client.get('/api/me').then((res) => res.data);
}

// Saves the user's name to their Firebase profile (users/{uid}).
export function updateMe({ firstName, lastName }) {
  return client.patch('/api/me', { firstName, lastName }).then((res) => res.data);
}

// Admin only: every unit in the organization, with nurseCount and roomCount.
export function getUnits() {
  return client.get('/api/units').then((res) => res.data);
}

// ---- Both roles. Admins get the whole organization; charge nurses get only
// their own unit (the backend scopes it). ----

// Counts for the Overview. Admin: { unitCount, nurseCount, neverLoggedInCount,
// currentShift: { date, shift, handoffCount } }. Charge:
// { reportSheetCount, unitCount: null, nurseCount, neverLoggedInCount }.
export function getOverview() {
  return client.get('/api/overview').then((res) => res.data);
}

export function getNurses() {
  return client.get('/api/nurses').then((res) => res.data);
}

// The nurse; charge nurses also get their current reportSheets.
export function getNurse(nurseId) {
  return client.get(`/api/nurses/${encodeURIComponent(nurseId)}`).then((res) => res.data);
}

// Admin only: the nurse's last 30 days of handoffs and adoption, aggregates
// only. { days, handoffCount, lastHandoffAt, activeShiftCount, shifts: { day,
// night: { handoffCount, averageMentionedPct } } | null, currentPatientCount,
// accountCreatedAt }.
export function getNurseActivity(nurseId) {
  return client.get(`/api/nurses/${encodeURIComponent(nurseId)}/activity`).then((res) => res.data);
}

// ---- Charge only. Admins never see individual report sheets. ----

// { units: [<the caller's unit, with its reportSheets>], unassigned: [] }.
// Each sheet's owner is { id, name, isMine }.
export function getReportSheets() {
  return client.get('/api/report-sheets').then((res) => res.data);
}

export function getReport(reportId) {
  return client.get(`/api/reports/${encodeURIComponent(reportId)}`).then((res) => res.data);
}

// Moves a patient's report sheet to another nurse on the unit, like the phone
// app's Transfer. Returns the updated sheet entry.
export function reassignReport(reportId, nurseId) {
  return client.post(`/api/reports/${encodeURIComponent(reportId)}/reassign`, { nurseId }).then((res) => res.data);
}

// ---- Admin only. Everything is scoped to the caller's organization. ----

// { id, name, rooms, nurses }
export function getUnit(unitId) {
  return client.get(`/api/units/${encodeURIComponent(unitId)}`).then((res) => res.data);
}

// Puts a nurse on a unit, or takes them off it with unitId null. A nurse is on
// at most one unit, and sees that unit's charge nurse dashboard. Returns the nurse.
export function setNurseUnit(nurseId, unitId) {
  return client.put(`/api/nurses/${encodeURIComponent(nurseId)}/unit`, { unitId: unitId || null }).then((res) => res.data);
}

// No password: the backend generates one and emails it to the new nurse.
export function createNurse({ firstName, lastName, email }) {
  return client.post('/api/nurses', { firstName, lastName, email }).then((res) => res.data);
}

export function deleteNurse(nurseId) {
  return client.delete(`/api/nurses/${encodeURIComponent(nurseId)}`);
}

export function getAdmins() {
  return client.get('/api/admins').then((res) => res.data);
}

// No password: the backend generates one and emails it to the new admin, or
// grants admin access to an existing Kabilah account in the organization.
export function createAdmin({ firstName, lastName, email }) {
  return client.post('/api/admins', { firstName, lastName, email }).then((res) => res.data);
}

export function deleteAdmin(adminId) {
  return client.delete(`/api/admins/${encodeURIComponent(adminId)}`);
}

// Aggregate handoff checklist results for one shift: { date, shift, timezone,
// windowStart, windowEnd, handoffCount, items: [{ name, mentionedPct,
// missedPct, handoffCount }] }. With no date or shift, the most recent shift.
export function getChecklistData({ date, shift, unitId } = {}) {
  const params = {};
  if (date) params.date = date;
  if (shift) params.shift = shift;
  if (unitId) params.unitId = unitId;
  return client.get('/api/data/checklist', { params }).then((res) => res.data);
}
